import { useCallback } from "react";

const RESET_INTERVAL_MS = 60 * 60 * 1000; // 1 hour

/**
 * Hook to manage a localStorage flag for error message firing, which resets after 1 hour.
 *
 * Usage:
 *   const { shouldShowError, setErrorFired } = useLocalStorageFlag();
 *   if (shouldShowError()) { ... setErrorFired(); ... }
 */
export function useLocalStorageFlag(flagKey: string) {
  // Returns true if the error message should be shown (not fired in the last hour)
  const shouldShowError = useCallback(() => {
    const firedAt = localStorage.getItem(flagKey);
    if (!firedAt) {
      return true;
    }
    const firedAtTime = parseInt(firedAt, 10);
    if (isNaN(firedAtTime)) {
      return true;
    }

    return Date.now() - firedAtTime > RESET_INTERVAL_MS;
  }, [flagKey]);

  // Sets the flag to the current time
  const setErrorFired = useCallback(() => {
    localStorage.setItem(flagKey, Date.now().toString());
  }, [flagKey]);

  // Optionally, a function to clear the flag
  const clearFlag = useCallback(() => {
    localStorage.removeItem(flagKey);
  }, [flagKey]);

  return { shouldShowError, setErrorFired, clearFlag };
}
